import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { compactSymbol, formatChange, formatPips, formatPrice, getPairFlags, midpointPrice, normalizeSymbol, toFinitePrice } from '@/lib/forexUtils';
import { useAuth } from '@/contexts/AuthContext';
import { chartTimeLabels, marketQuoteStatusLabel, marketSourceLabel, marketTimeLabel } from '@/lib/marketEvidence';
import { useMarketAccount } from '@/contexts/useMarketAccount';
import MarketAccountPicker from '@/components/MarketAccountPicker';
import forexApi, { sameMarketSource, type MarketProjectionContext, type MarketTickerDTO } from '@/services/forexApi';
import SortMenu, { type SortMode } from './components/SortMenu';
import FilterPanel, { type FilterState, DEFAULT_FILTER } from './components/FilterPanel';

type TimeframeKey = '1H' | '4H' | '1D' | '1W';
const TIMEFRAMES: TimeframeKey[] = ['1H', '4H', '1D', '1W'];

interface MarketPair {
  pair: string;
  flags: string;
  price: number | null;
  bid: number | null;
  ask: number | null;
  sourceId?: string;
  marketContext?: MarketProjectionContext | null;
  quoteStatus?: string;
  quoteTime?: number | null;
  priceDigits?: number;
  change: number;
  pips: number;
  changeUnitLabel: string;
  spread: number;
  spreadUnitLabel: string;
  favorite: boolean;
  agentCount: number;
  positionCount: number;
  signalStatus: 'BUY' | 'SELL' | 'HOLD' | 'none' | 'protected';
  history: number[];
  historyByTf: Record<TimeframeKey, number[]>;
  historyTimesByTf: Record<TimeframeKey, number[]>;
  historyStatusByTf: Record<TimeframeKey, 'loading' | 'ready' | 'error'>;
}

function emptyHistoryByTf(): Record<TimeframeKey, number[]> {
  return {
    '1H': [],
    '4H': [],
    '1D': [],
    '1W': [],
  };
}

function emptyHistoryStatusByTf(): Record<TimeframeKey, 'loading' | 'ready' | 'error'> {
  return { '1H': 'loading', '4H': 'loading', '1D': 'loading', '1W': 'loading' };
}

const TABS = ['全部', '自选', '有AI 智能体', '有持仓', '有信号'] as const;
type TabType = typeof TABS[number];

const SORT_BUTTON_LABEL: Record<SortMode, string> = {
  default: '排序',
  change_desc: '涨幅',
  change_asc: '跌幅',
  spread_asc: '点差',
  agent_first: '智能',
  position_first: '持仓',
};

const SIGNAL_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  BUY: { bg: '#DCFCE7', text: '#16A34A', label: 'BUY' },
  SELL: { bg: '#FFE4E6', text: '#E11D48', label: 'SELL' },
  HOLD: { bg: '#EEF2FF', text: '#475569', label: 'HOLD' },
  none: { bg: '#F3F4F6', text: '#6B7280', label: '无信号' },
  protected: { bg: '#FEF3C7', text: '#D97706', label: '事件保护' },
};

function StatusChip({ status }: { status: string }) {
  const cfg = SIGNAL_CONFIG[status] || SIGNAL_CONFIG.none;
  return (
    <span
      className="px-2 py-0.5 rounded-md text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.text }}
    >
      {cfg.label}
    </span>
  );
}

function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (!data.length) return null;
  const w = 60;
  const h = 24;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="flex-shrink-0">
      <polyline fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" points={pts} />
    </svg>
  );
}

let chartGradientId = 0;
function FeaturedChart({ data, timestamps, tf }: { data: number[]; timestamps: number[]; tf: TimeframeKey }) {
  if (!data.length) return null;
  const gradientId = `fArea-${++chartGradientId}`;
  const width = 320;
  const height = 120;
  const pad = { t: 8, r: 48, b: 20, l: 0 };
  const cw = width - pad.l - pad.r;
  const ch = height - pad.t - pad.b;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = pad.l + (i / (data.length - 1)) * cw;
    const y = pad.t + ch - ((v - min) / range) * ch;
    return `${x},${y}`;
  }).join(' ');
  const area = `M${pad.l},${pad.t + ch} L${pts.split(' ')[0]} L${pts.replace(/ /g, ' L')} L${pad.l + cw},${pad.t + ch} Z`;
  const labels = chartTimeLabels(timestamps, tf);
  const cp = data[data.length - 1];
  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" className="w-full" style={{ height: 'auto' }}>
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366F1" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#6366F1" stopOpacity="0.01" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} />
      <polyline fill="none" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" points={pts} />
      {(() => {
        const lx = pad.l + cw;
        const ly = pad.t + ch - ((cp - min) / range) * ch;
        return (
          <>
            <circle cx={lx} cy={ly} r="4" fill="#6366F1" />
            <text x={width - 4} y={ly + 4} textAnchor="end" fill="#6366F1" fontSize="10" fontWeight="600">{cp.toFixed(5)}</text>
          </>
        );
      })()}
      {labels.map((l, i) => (
        <text key={i} x={pad.l + (i / (labels.length - 1)) * cw} y={height - 4} textAnchor="middle" fill="#94A3B8" fontSize="9">{l}</text>
      ))}
    </svg>
  );
}

function TopOfBook({ pair, bid, ask, digits, spread, spreadUnit }: {
  pair: string; bid: number | null; ask: number | null; digits?: number;
  spread: number; spreadUnit?: string;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 text-xs">
      <div><span className="text-text-secondary">买价</span><div className="font-semibold text-[#10B981]">{formatPrice(pair, bid, digits)}</div></div>
      <div><span className="text-text-secondary">卖价</span><div className="font-semibold text-[#F43F5E]">{formatPrice(pair, ask, digits)}</div></div>
      <div className="col-span-2 text-text-secondary">点差 {spread} {spreadUnit || 'point'} · 服务端报价，无订单簿手数</div>
    </div>
  );
}

// ── Sort helpers ──
function computeSortScore(p: MarketPair): number {
  let score = 0;
  if (p.agentCount > 0) score += 200;
  if (p.positionCount > 0) score += 100;
  if (p.signalStatus !== 'none') score += 50;
  score += Math.abs(p.change) * 10;
  return score;
}

function applySort(pairs: MarketPair[], mode: SortMode): MarketPair[] {
  const copy = [...pairs];
  switch (mode) {
    case 'default':
      copy.sort((a, b) => computeSortScore(b) - computeSortScore(a));
      break;
    case 'change_desc':
      copy.sort((a, b) => b.change - a.change);
      break;
    case 'change_asc':
      copy.sort((a, b) => a.change - b.change);
      break;
    case 'spread_asc':
      copy.sort((a, b) => a.spread - b.spread);
      break;
    case 'agent_first':
      copy.sort((a, b) => {
        if (b.agentCount !== a.agentCount) return b.agentCount - a.agentCount;
        return computeSortScore(b) - computeSortScore(a);
      });
      break;
    case 'position_first':
      copy.sort((a, b) => {
        if (b.positionCount !== a.positionCount) return b.positionCount - a.positionCount;
        return computeSortScore(b) - computeSortScore(a);
      });
      break;
  }
  return copy;
}

function applyFilter(pairs: MarketPair[], f: FilterState): MarketPair[] {
  let r = pairs;
  if (f.signal !== 'all') r = r.filter((p) => p.signalStatus === f.signal);
  if (f.spread === 'lte1') r = r.filter((p) => p.spread <= 1.0);
  if (f.spread === 'lte1_5') r = r.filter((p) => p.spread <= 1.5);
  if (f.status === 'hasAgent') r = r.filter((p) => p.agentCount > 0);
  if (f.status === 'hasPosition') r = r.filter((p) => p.positionCount > 0);
  if (f.status === 'protected') r = r.filter((p) => p.signalStatus === 'protected');
  return r;
}

function countActiveFilters(f: FilterState): number {
  let c = 0;
  if (f.signal !== 'all') c++;
  if (f.spread !== 'all') c++;
  if (f.status !== 'all') c++;
  return c;
}

export default function MarketPage() {
  const { scopeKey } = useMarketAccount();
  return <MarketPageContent key={scopeKey} />;
}

function MarketPageContent() {
  const { scope } = useMarketAccount();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [pairs, setPairs] = useState<MarketPair[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('全部');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOpen, setSortOpen] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('default');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterState, setFilterState] = useState<FilterState>(DEFAULT_FILTER);
  const [selectedPair, setSelectedPair] = useState<string>('');
  const [selectedTf, setSelectedTf] = useState<TimeframeKey>('1H');
  const [showDepth, setShowDepth] = useState(false);
  const [quoteState, setQuoteState] = useState<'loading' | 'ready' | 'error'>('loading');

  const loadQuotes = useCallback((signal: AbortSignal) => {
    return forexApi.listMarketQuotes(accessToken, 'symbol_asc', scope, signal)
      .then((tickers) => {
        if (signal.aborted) return;
        setPairs((previous) => {
          const previousByPair = new Map(previous.map((item) => [item.pair, item]));
          const nextPairs = tickers.map((ticker: MarketTickerDTO, index) => {
            const pair = normalizeSymbol(ticker.symbol);
            const previousItem = previousByPair.get(pair);
            const previousSource = sameMarketSource(previousItem?.marketContext, ticker.market_context) ? previousItem : undefined;
            const signalStatus = ticker.signal_status || 'none';
            return {
              pair,
              flags: getPairFlags(ticker.symbol),
              price: midpointPrice(ticker.bid, ticker.ask),
              bid: toFinitePrice(ticker.bid),
              ask: toFinitePrice(ticker.ask),
              sourceId: ticker.source_id,
              marketContext: ticker.market_context,
              quoteStatus: ticker.freshness_status || ticker.quote_status,
              quoteTime: ticker.quote_time,
              priceDigits: ticker.digits ?? undefined,
              change: ticker.change_pct,
              pips: ticker.change_pips,
              changeUnitLabel: ticker.change_unit_label ?? 'point',
              spread: toFinitePrice(ticker.spread_units) ?? ticker.spread_points,
              spreadUnitLabel: ticker.spread_unit_label ?? 'point',
              favorite: previousItem?.favorite ?? index === 0,
              agentCount: ticker.agent_count || 0,
              positionCount: ticker.position_count || 0,
              signalStatus,
              history: previousSource?.history ?? [],
              historyByTf: previousSource?.historyByTf ?? emptyHistoryByTf(),
              historyTimesByTf: previousSource?.historyTimesByTf ?? emptyHistoryByTf(),
              historyStatusByTf: previousSource?.historyStatusByTf ?? emptyHistoryStatusByTf(),
            } satisfies MarketPair;
          });
          return nextPairs;
        });
        setSelectedPair((current) => current || normalizeSymbol(tickers[0]?.symbol || ''));
        setQuoteState('ready');
      })
      .catch(() => {
        if (signal.aborted) return;
        setPairs([]);
        setQuoteState('error');
      });
  }, [accessToken, scope]);

  useEffect(() => {
    const controller = new AbortController();
    let busy = false;
    const poll = async () => {
      if (busy || controller.signal.aborted) return;
      busy = true;
      try { await loadQuotes(controller.signal); } finally { busy = false; }
    };
    void poll();
    const timer = window.setInterval(() => { void poll(); }, 8000);
    return () => { controller.abort(); window.clearInterval(timer); };
  }, [loadQuotes]);

  // Re-fetch history only when symbols or their immutable source identities change.
  const sourcesKey = JSON.stringify(pairs.map(pair => [pair.pair, pair.marketContext]));
  useEffect(() => {
    const sources = JSON.parse(sourcesKey) as [string, MarketProjectionContext | null][];
    if (!scope || !sources.length) return;
    const controller = new AbortController();
    Promise.all(sources.map(([pairName, source]) =>
      forexApi.getMarketTickerHistory(compactSymbol(pairName), selectedTf, accessToken, scope, source, controller.signal)
        .then(history => ({ pairName, source, failed: false,
          points: history.items.filter(item => toFinitePrice(item.mid) != null && Number.isFinite(item.timestamp) && item.timestamp > 0)
            .map(item => ({ mid: toFinitePrice(item.mid) as number, timestamp: item.timestamp })) }))
        .catch(() => ({ pairName, source, points: [] as { mid: number; timestamp: number }[], failed: true }))
    )).then(histories => {
      if (controller.signal.aborted) return;
      setPairs(previous => previous.map(pair => {
        const history = histories.find(item => item.pairName === pair.pair && sameMarketSource(pair.marketContext, item.source));
        if (!history) return pair;
        return { ...pair,
          history: selectedTf === '1H' ? history.points.map(point => point.mid) : pair.history,
          historyByTf: { ...pair.historyByTf, [selectedTf]: history.points.map(point => point.mid) },
          historyTimesByTf: { ...pair.historyTimesByTf, [selectedTf]: history.points.map(point => point.timestamp) },
          historyStatusByTf: { ...pair.historyStatusByTf, [selectedTf]: history.failed ? 'error' : 'ready' },
        };
      }));
    });
    return () => controller.abort();
  }, [accessToken, selectedTf, sourcesKey, scope]);

  // Tab filter → filter → sort
  const filtered = useMemo(() => {
    let r = pairs;
    // Tab filter
    if (activeTab === '自选') r = r.filter((p) => p.favorite);
    if (activeTab === '有AI 智能体') r = r.filter((p) => p.agentCount > 0);
    if (activeTab === '有持仓') r = r.filter((p) => p.positionCount > 0);
    if (activeTab === '有信号') r = r.filter((p) => p.signalStatus !== 'none');
    // Search filter
    if (searchQuery.trim()) {
      r = r.filter((p) => p.pair.toLowerCase().includes(searchQuery.trim().toLowerCase()));
    }
    // Filter panel
    r = applyFilter(r, filterState);
    // Sort
    r = applySort(r, sortMode);
    return r;
  }, [pairs, activeTab, searchQuery, filterState, sortMode]);

  const featured = pairs.find((p) => p.pair === selectedPair) || pairs[0] || null;

  const toggleFavorite = (pairName: string) => {
    setPairs((prev) => prev.map((p) => (p.pair === pairName ? { ...p, favorite: !p.favorite } : p)));
  };

  const getSubStatus = (p: MarketPair) => {
    if (p.positionCount > 0) return `${p.positionCount} 个持仓`;
    if (p.agentCount > 0) return `${p.agentCount} 个 AI 智能体监控中`;
    if (p.signalStatus === 'protected') return '事件保护中';
    return '无持仓';
  };

  const upCount = pairs.filter((p) => p.change >= 0).length;
  const downCount = pairs.filter((p) => p.change < 0).length;
  const activeFilterCount = countActiveFilters(filterState);

  return (
    <div className="min-h-screen" style={{ background: '#F7F8FC', paddingBottom: '84px' }}>
      {/* Header */}
      <div
        className="bg-white"
        style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
      >
        <div className="px-5 pb-3" style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)' }}>
          {/* Title row */}
          <div className="flex items-center justify-between mb-3">
            <button onClick={() => navigate(-1)} className="w-8 h-8 flex items-center justify-center">
              <i className="ri-arrow-left-s-line text-text-primary text-xl" />
            </button>
            <h1 className="text-text-primary font-bold text-lg">行情</h1>
            <div className="flex items-center gap-2">
              <button onClick={() => setSearchOpen((v) => !v)} className="w-8 h-8 flex items-center justify-center">
                <i className={`${searchOpen ? 'ri-close-line' : 'ri-search-line'} text-text-primary text-lg`} />
              </button>
            </div>
          </div>

          <div className="mb-3"><MarketAccountPicker /></div>

          {/* Search bar */}
          {searchOpen && (
            <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl" style={{ background: '#F3F4F6' }}>
              <i className="ri-search-line text-gray-400 text-sm" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="搜索货币对"
                className="flex-1 bg-transparent text-sm text-text-primary outline-none placeholder-gray-400"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')}>
                  <i className="ri-close-circle-fill text-gray-400 text-sm" />
                </button>
              )}
            </div>
          )}

          {/* Filter Tabs */}
          <div className="flex flex-wrap gap-2">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all"
                style={
                  activeTab === tab
                    ? {
                        background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
                        color: 'white',
                        boxShadow: '0 2px 8px rgba(99,102,241,0.25)',
                      }
                    : { background: 'white', color: '#6B7280', border: '1px solid #E5E7EB' }
                }
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sort + filter row */}
      {!searchOpen && (
        <div className="px-5 py-3 flex items-center gap-2">
          <div
            onClick={() => setSearchOpen(true)}
            className="min-w-0 flex-1 h-10 flex items-center gap-2 px-3 rounded-2xl bg-white active:bg-gray-50 cursor-pointer"
            style={{ border: '1px solid #E5E7EB' }}
          >
            <i className="ri-search-line text-gray-400 text-sm" />
            <input
              aria-label="搜索货币对"
              type="text"
              value={searchQuery}
              onFocus={() => setSearchOpen(true)}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="搜索货币对"
              className="min-w-0 flex-1 bg-transparent text-sm text-text-primary outline-none placeholder-gray-400"
            />
          </div>
          <div className="relative">
            <button
              onClick={() => { setSortOpen(!sortOpen); setFilterOpen(false); }}
              className="h-10 w-[68px] flex items-center justify-center gap-1 rounded-2xl bg-white text-xs transition-colors active:bg-gray-50 whitespace-nowrap"
              style={{
                border: '1px solid #E5E7EB',
                color: sortMode !== 'default' ? '#6366F1' : '#6B7280',
                fontWeight: sortMode !== 'default' ? 600 : 400,
              }}
            >
              <span>{SORT_BUTTON_LABEL[sortMode]}</span>
              <i className="ri-arrow-up-down-line text-xs" />
            </button>
            {sortOpen && (
              <SortMenu
                value={sortMode}
                onChange={setSortMode}
                onClose={() => setSortOpen(false)}
              />
            )}
          </div>
          <div className="relative">
            <button
              onClick={() => { setFilterOpen(!filterOpen); setSortOpen(false); }}
              className="h-10 w-[68px] flex items-center justify-center gap-1 rounded-2xl bg-white text-xs transition-colors active:bg-gray-50 whitespace-nowrap"
              style={{
                border: activeFilterCount > 0 ? '1px solid #C7D2FE' : '1px solid #E5E7EB',
                color: activeFilterCount > 0 ? '#6366F1' : '#6B7280',
                fontWeight: activeFilterCount > 0 ? 600 : 400,
              }}
            >
              <i className="ri-filter-3-line text-xs" />
              <span>筛选{activeFilterCount > 0 ? activeFilterCount : ''}</span>
            </button>
          </div>
        </div>
      )}

      {/* Filter panel */}
      {filterOpen && (
        <FilterPanel
          value={filterState}
          onChange={setFilterState}
          onClose={() => setFilterOpen(false)}
        />
      )}

      {/* Featured pair card */}
      {featured && (
        <div className="px-5 mb-4">
          <div className="bg-white rounded-card p-4" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            {/* Header: pair name + favorite */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-xl">{featured.flags}</span>
                <span className="text-text-primary font-bold text-base">{featured.pair}</span>
                <i
                  className={featured.favorite ? 'ri-star-fill text-warn' : 'ri-star-line text-gray-300'}
                  onClick={() => toggleFavorite(featured.pair)}
                  style={{ fontSize: '16px', cursor: 'pointer' }}
                />
              </div>
            </div>

            {/* Price row */}
            <div className="flex items-baseline gap-3 mb-3">
              <span className="text-text-primary font-bold text-3xl tracking-tight">{formatPrice(featured.pair, featured.price, featured.priceDigits)}</span>
              <div className="flex flex-col">
                <span className="text-sm font-semibold" style={{ color: featured.change >= 0 ? '#10B981' : '#F43F5E' }}>
                  {formatPips(featured.pips, featured.changeUnitLabel)}
                </span>
                <span className="text-xs" style={{ color: featured.change >= 0 ? '#10B981' : '#F43F5E' }}>
                  {formatChange(featured.change)}
                </span>
              </div>
            </div>

            {/* Chart */}
            <div className="mb-3">
              {featured.historyStatusByTf[selectedTf] === 'error' ? (
                <div className="h-[120px] flex items-center justify-center text-xs text-gray-400">历史行情暂不可用</div>
              ) : featured.historyStatusByTf[selectedTf] === 'loading' ? (
                <div className="h-[120px] flex items-center justify-center text-xs text-gray-400">正在同步历史行情...</div>
              ) : featured.historyByTf[selectedTf].length >= 2 ? (
                <FeaturedChart data={featured.historyByTf[selectedTf]} timestamps={featured.historyTimesByTf[selectedTf]} tf={selectedTf} />
              ) : (
                <div className="h-[120px] flex items-center justify-center text-xs text-gray-400">数据积累中</div>
              )}
            </div>

            {/* Depth toggle + depth section */}
            <button
              onClick={() => setShowDepth(!showDepth)}
              className="flex items-center gap-1.5 text-xs text-text-secondary mb-2"
            >
              <i className="ri-stack-line" style={{ color: showDepth ? '#6366F1' : undefined }} />
              买卖报价
              {showDepth ? <i className="ri-arrow-up-s-line text-xs" /> : <i className="ri-arrow-down-s-line text-xs" />}
            </button>
            {showDepth && (
              <div className="mb-3 px-2 py-2 rounded-xl" style={{ background: '#F9FAFB' }}>
                <TopOfBook pair={featured.pair} bid={featured.bid} ask={featured.ask} digits={featured.priceDigits} spread={featured.spread} spreadUnit={featured.spreadUnitLabel} />
              </div>
            )}

            {/* Footer meta */}
            <div className="flex items-center gap-2 text-xs text-text-secondary">
              <span>Spread {featured.spread} {featured.spreadUnitLabel}</span>
              <span className="text-gray-300">·</span>
              <span className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#10B981' }} />
                {marketSourceLabel(featured.sourceId)} · {marketQuoteStatusLabel(featured.quoteStatus)} · {marketTimeLabel(featured.quoteTime)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Global timeframe selector + list */}
      <div className="px-5">
        {/* Header row: title + count + timeframe */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-text-primary font-bold text-base">货币对列表</span>
            <span className="text-text-secondary text-xs">{filtered.length} 个品种</span>
          </div>
          {/* Inline quick stats */}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-[#10B981]">涨 {upCount}</span>
            <span className="text-[#F43F5E]">跌 {downCount}</span>
          </div>
        </div>

        {/* Global timeframe segmented control */}
        <div className="flex mb-3 p-0.5 rounded-full" style={{ background: '#F3F4F6' }}>
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf}
              onClick={() => setSelectedTf(tf)}
              className="flex-1 py-1.5 rounded-full text-xs font-semibold transition-all"
              style={{
                background: selectedTf === tf ? 'white' : 'transparent',
                color: selectedTf === tf ? '#6366F1' : '#94A3B8',
                boxShadow: selectedTf === tf ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              }}
            >
              {tf}
            </button>
          ))}
        </div>

        {/* Filter summary bar — when filters active */}
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 mb-3 px-3 py-2 rounded-xl" style={{ background: '#EEF2FF' }}>
            <i className="ri-filter-3-line text-indigo-400 text-xs" />
            <span className="text-indigo-600 text-xs font-medium">
              已筛选 {filtered.length} 个品种
            </span>
            <button
              onClick={() => setFilterState(DEFAULT_FILTER)}
              className="ml-auto text-indigo-400 text-xs font-medium flex items-center gap-1"
            >
              <i className="ri-close-line text-[11px]" />
              重置筛选
            </button>
          </div>
        )}

        {quoteState === 'loading' && pairs.length === 0 && <p className="py-4 text-center text-xs text-gray-400">正在同步行情...</p>}
        {quoteState === 'error' && <p role="status" className="py-4 text-center text-xs text-gray-500">行情暂不可用，正在重试</p>}
        {quoteState === 'ready' && pairs.length === 0 && <p className="py-4 text-center text-xs text-gray-400">暂无可确认的行情来源</p>}

        {/* Pair list */}
        {filtered.length > 0 ? (
          <div className="bg-white rounded-card overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            {filtered.map((p, idx) => {
              const isUp = p.change >= 0;
              const sparkColor = isUp ? '#10B981' : '#F43F5E';
              const tfHistory = p.historyByTf[selectedTf];
              return (
                <div
                  key={p.pair}
                  className="flex items-center px-4 py-3.5 cursor-pointer active:bg-gray-50 transition-colors"
                  style={{ borderBottom: idx < filtered.length - 1 ? '1px solid #F3F4F6' : 'none' }}
                  onClick={() => setSelectedPair(p.pair)}
                >
                  {/* Left: flags + pair + status */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-base">{p.flags}</span>
                      <span className="text-text-primary font-bold text-sm">{p.pair}</span>
                      <i
                        className={p.favorite ? 'ri-star-fill text-warn' : 'ri-star-line text-gray-300'}
                        onClick={(e) => { e.stopPropagation(); toggleFavorite(p.pair); }}
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                    <span className="text-text-secondary text-xs">{getSubStatus(p)}</span>
                  </div>

                  {/* Middle: price + change + pips */}
                  <div className="flex-shrink-0 text-right mr-3">
                    <div className="text-text-primary font-bold text-sm">{formatPrice(p.pair, p.price, p.priceDigits)}</div>
                    <div className="flex items-center justify-end gap-2 mt-0.5">
                      <span className="text-xs font-medium" style={{ color: isUp ? '#10B981' : '#F43F5E' }}>
                        {formatChange(p.change)}
                      </span>
                      <span className="text-xs" style={{ color: isUp ? '#10B981' : '#F43F5E' }}>
                        {formatPips(p.pips, p.changeUnitLabel)}
                      </span>
                    </div>
                  </div>

                  {/* Right: spread + status chip + sparkline */}
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-text-secondary">Spread {p.spread} {p.spreadUnitLabel}</span>
                      <StatusChip status={p.signalStatus} />
                    </div>
                    {p.historyStatusByTf[selectedTf] === 'error' ? (
                      <span className="text-[10px] text-gray-400">历史暂不可用</span>
                    ) : tfHistory.length >= 2 ? (
                      <Sparkline data={tfHistory} color={sparkColor} />
                    ) : (
                      <span className="text-[10px] text-gray-400">数据积累中</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : pairs.length > 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 flex items-center justify-center rounded-full mb-3" style={{ background: '#F3F4F6' }}>
              <i className="ri-search-line text-gray-400 text-2xl" />
            </div>
            <p className="text-text-secondary text-sm">未找到相关货币对</p>
          </div>
        ) : null}
      </div>

      <div style={{ height: '20px' }} />
    </div>
  );
}
